import { MouseEvent } from "react";
import SectionTitle from "./SectionTitle";
import ScrollReveal from "./ScrollReveal";
import { IconType } from "react-icons";
import { FaGraduationCap, FaLaptopCode, FaArrowRight } from "react-icons/fa";

interface Project {
  title: string;
  subtitle: string;
  category: string;
  description: string;
  points: string[];
  tech: string[];
  icon: IconType;
  accent: string; // gradient classes for the cover panel
  link?: string;
  linkLabel?: string;
}

const projects: Project[] = [
  {
    title: "EaseOnTap",
    subtitle: "Attendance & Student Management Dashboard",
    category: "EdTech",
    description:
      "A role-based administrative dashboard for educational institutions to manage attendance tracking, student records, and academic data workflows.",
    points: [
      "Built interactive dashboards for teachers and administrators to manage student data, attendance, and project activities.",
      "Integrated REST APIs for real-time updates across attendance tracking and student management modules.",
      "Designed modular, reusable UI components for a scalable dashboard and consistent experience.",
      "Optimized data handling and rendering for large student datasets.",
    ],
    tech: ["React", "TypeScript", "REST APIs", "Redux", "Tailwind CSS"],
    icon: FaGraduationCap,
    accent: "from-teal-500 to-cyan-500",
  },
  {
    title: "Developer Portfolio",
    subtitle: "This site — btw-itsarsh.in",
    category: "Personal",
    description:
      "A responsive personal portfolio showcasing projects, experience, and technical expertise, built with a component-driven architecture and a focus on performance.",
    points: [
      "Component-driven architecture for clean maintainability and scalability.",
      "Dark / light mode with persistent preferences and smooth transitions.",
      "Accessibility best practices and responsive design across all screen sizes.",
    ],
    tech: ["React", "TypeScript", "Tailwind CSS", "React Icons"],
    icon: FaLaptopCode,
    accent: "from-cyan-500 to-sky-500",
    link: "https://www.btw-itsarsh.in",
    linkLabel: "Visit live site",
  },
];

// Subtle dot-grid overlay used on each project cover panel.
const dotPattern = {
  backgroundImage:
    "radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)",
  backgroundSize: "16px 16px",
};

// Track the cursor so the spotlight follows it across the card.
const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * 100;
  const y = ((e.clientY - rect.top) / rect.height) * 100;
  e.currentTarget.style.setProperty("--mx", `${x}%`);
  e.currentTarget.style.setProperty("--my", `${y}%`);
};

const Projects = () => {
  return (
    <section id="projects" className="py-14 scroll-mt-16">
      <ScrollReveal>
        <SectionTitle
          title="Projects"
          subtitle="A selection of products and interfaces I've designed and built."
        />
      </ScrollReveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map((project, index) => {
          const Icon = project.icon;
          return (
            <ScrollReveal key={project.title} delay={index * 150}>
              <div className="group relative h-full">
                {/* gradient glow-border, revealed on hover */}
                <div className="pointer-events-none absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-400 to-sky-500 opacity-0 blur-[6px] transition-opacity duration-500 group-hover:opacity-40" />

                {/* card */}
                <div
                  onMouseMove={handleSpotlight}
                  className="relative z-10 flex flex-col h-full rounded-2xl overflow-hidden bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-800 card-hover"
                >
                  {/* cursor-follow spotlight */}
                  <div className="spotlight pointer-events-none absolute inset-0 z-30" />

                  {/* Cover panel — the signature element */}
                  <div
                    className={`shimmer relative h-36 overflow-hidden bg-gradient-to-br ${project.accent}`}
                  >
                    {/* dot grid */}
                    <div className="absolute inset-0" style={dotPattern} />
                    {/* soft glow that reacts to hover */}
                    <div className="absolute -top-10 -right-8 w-36 h-36 rounded-full bg-white/25 blur-2xl transition-transform duration-500 group-hover:scale-125" />

                    {/* category badge */}
                    <span className="absolute top-4 left-5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide text-white bg-white/15 ring-1 ring-white/25 backdrop-blur-sm">
                      {project.category}
                    </span>

                    {/* live indicator */}
                    {project.link && (
                      <span className="absolute top-4 right-5 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-white bg-white/15 ring-1 ring-white/25 backdrop-blur-sm">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                        </span>
                        Live
                      </span>
                    )}

                    {/* glassy icon tile — kept inside the cover so it's never clipped */}
                    <div className="absolute bottom-4 left-5 w-14 h-14 rounded-2xl flex items-center justify-center text-white text-2xl bg-white/20 ring-1 ring-white/40 backdrop-blur-md shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105">
                      <Icon />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="relative z-20 flex flex-col flex-grow p-5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {project.title}
                    </h3>
                    <p className="text-sm text-teal-600 dark:text-teal-400 font-medium">
                      {project.subtitle}
                    </p>

                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mt-3">
                      {project.description}
                    </p>

                    <ul className="space-y-2 mt-4 flex-grow">
                      {project.points.map((point, i) => (
                        <li
                          key={i}
                          className="flex gap-2 text-gray-700 dark:text-gray-300 text-sm leading-relaxed"
                        >
                          <span className="text-teal-500 mt-0.5 flex-shrink-0">
                            &#9656;
                          </span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Footer: tech + CTA */}
                    <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((t) => (
                          <span
                            key={t}
                            className="px-2.5 py-1 text-xs rounded-md bg-teal-500/10 text-teal-600 dark:text-teal-400 font-medium"
                          >
                            {t}
                          </span>
                        ))}
                      </div>

                      {project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/link inline-flex items-center gap-2 mt-4 text-sm font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-500 transition-colors"
                          aria-label={`${project.linkLabel} — ${project.title}`}
                        >
                          {project.linkLabel}
                          <FaArrowRight className="text-xs transition-transform duration-300 group-hover/link:translate-x-1" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          );
        })}
      </div>
    </section>
  );
};

export default Projects;
